/**
 * The CommentPopup component consists of 4 parts; Container, Body, Form, Image Input Row.
 *  1. Container (this file)
 *     - This is the main component that handles the popup's position and visibility.
 *     - Will also contain the Body and Form.
 *     - It also handles new creation of comment and reply.
 *     - The component embedded in this file is for creation of new comment/reply.
 *  2. Body (CommentBody)
 *     - This is the component that handles the display of comment and reply body.
 *     - It also handles the editing and deletion of comment and reply.
 *     - Contains the menu item for comment / reply actions (edit, delete, etc.)
 *     - The CommentInput embedded here is for editing comment/reply.
 *  3. Form (CommentInput)
 *     - Handles both comment and reply, create and edit as well as file management.
 *  4. Iamge Input Row (CommentInputImage)
 *    - Handles the display of image selected to be uploaded and their caption.
 *    - The component is only for a single image looped in CommentInput.
 */
import { FC } from 'react'

import { Box, Button, HStack, Popover, Portal, Spacer, Spinner, Text, VStack } from '@chakra-ui/react'
import { useAppSelector } from '@editor/store/app'

import { CommentBody } from './components/CommentBody'
import { CommentInput } from './components/CommentInput'
import { CommentPopupProps, useCommentPopup } from './hooks/commentPopup'

// drei cannot load styles of chakra-ui
const BUTTON_STYLE = {
  width: 'auto',
  lineHeight: 1.2,
  height: '32px',
  fontWeight: 'bold',
  borderRadius: 'var(--chakra-radii-md)',
  paddingX: '12px',
}
const PRIMARY_BUTTON_STYLE = {
  ...BUTTON_STYLE,
  backgroundColor: '{colors.primary.500}',
  color: 'white',
  _hover: { backgroundColor: '{colors.primary.600}' },
  _disabled: { backgroundColor: '{colors.primary.300}' },
}
const SECONDARY_BUTTON_STYLE = {
  ...BUTTON_STYLE,
  borderColor: '{colors.gray2.200}',
  borderWidth: 1,
  _hover: { backgroundColor: '{colors.gray2.100}' },
  _disabled: { backgroundColor: 'white', color: '{colors.gray2.400}' },
}

const CommentPopup: FC<CommentPopupProps> = (props) => {
  const { comment, onMove } = props
  const {
    isDisabled,
    isLoading,
    isNewComment,
    isInputShown,
    setIsLoading,
    onCancelInput,
    onSaved,
    handleClose,
    showReplyForm,
    fetchReplies,
  } = useCommentPopup(props)

  // Store
  const isThreadsLoading = useAppSelector((state) => state.comments.isThreadsLoading)

  if (!comment) return null

  return (
    <Popover.Root open>
      {/* Just for positioning the popover, trigger event will be handled by toolbar */}
      <Popover.Trigger asChild>
        <Box style={{ left: '-8px', right: 0, top: 0, position: 'absolute', width: 0, height: 0 }} />
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            backgroundColor="white"
            borderRadius="6px"
            maxWidth="320px"
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
          >
            <Popover.Arrow />
            <Popover.Body wordBreak="break-word" padding={0}>
              <VStack>
                {isThreadsLoading ? (
                  <HStack p={4}>
                    <Spinner size="sm" />
                    <Text>Loading...</Text>
                  </HStack>
                ) : (
                  <VStack overflow="auto" maxHeight="360px" p="8px" w="100%">
                    {/* ## MAIN THREAD */}
                    {!isNewComment && <CommentBody comment={comment} onLoading={setIsLoading} onMove={onMove} />}

                    {/* ## SHOW COMMENTS BUTTON */}
                    {!comment.replies?.length && comment?.reply_amount ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchReplies}
                        alignSelf="flex-start"
                        disabled={isLoading}
                        {...SECONDARY_BUTTON_STYLE}
                        py="6px"
                      >
                        {comment.reply_amount}件の返信を表示
                      </Button>
                    ) : null}

                    {/* ## REPLIES */}
                    {!!comment.replies?.length &&
                      comment.replies.map((reply) => (
                        <CommentBody key={reply.reply_id} comment={comment} reply={reply} onLoading={setIsLoading} />
                      ))}

                    {/* ## COMMENT FORM */}
                    {/* Only to be used when adding new comment or reply */}
                    {isInputShown && (
                      <CommentInput
                        parentComment={comment}
                        isDisabled={isDisabled}
                        onCancel={onCancelInput}
                        onSaved={onSaved}
                      />
                    )}
                  </VStack>
                )}

                {/* ## Buttons for showing add reply form and close modal ## */}
                {!isInputShown && (
                  <HStack alignSelf="stretch" p="8px" pt={0} w="100%">
                    {!isThreadsLoading && (
                      <Button
                        colorPalette="blue"
                        size="sm"
                        onClick={() => {
                          if (!isDisabled) {
                            showReplyForm()
                          }
                        }}
                        disabled={isDisabled || isLoading}
                        {...PRIMARY_BUTTON_STYLE}
                      >
                        返信
                      </Button>
                    )}
                    <Spacer />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!isDisabled) handleClose()
                      }}
                      disabled={isDisabled || isLoading}
                      {...SECONDARY_BUTTON_STYLE}
                    >
                      閉じる
                    </Button>
                  </HStack>
                )}
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

export default CommentPopup
